import { t } from "@lingui/macro";
import { Envelope, X } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useEffect, useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { useResendVerificationEmail } from "@/client/services/auth";
import { useUser } from "@/client/services/user";

const BANNER_DISMISSED_KEY = "careerbldr:verify-banner-dismissed";

export const EmailVerificationBanner = () => {
  const { user } = useUser();
  const { resendVerificationEmail, loading } = useResendVerificationEmail();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (!user || user.emailVerified) return;
    const wasDismissed = sessionStorage.getItem(BANNER_DISMISSED_KEY) === "1";
    if (!wasDismissed) setDismissed(false);
  }, [user]);

  if (!user || user.emailVerified || dismissed) return null;

  const userEmail = user.email;

  const handleDismiss = () => {
    sessionStorage.setItem(BANNER_DISMISSED_KEY, "1");
    setDismissed(true);
  };

  const handleResend = async () => {
    try {
      await resendVerificationEmail();
      toast({
        variant: "success",
        title: t`Verification email sent`,
        description: t`Check your inbox for the verification link.`,
      });
    } catch {
      toast({
        variant: "error",
        title: t`Couldn't send the email`,
        description: t`Please try again in a moment.`,
      });
    }
  };

  return (
    <div
      className={cn(
        "relative mb-4 flex flex-col items-start gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center",
        "border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-950/20",
      )}
    >
      <div className="flex flex-1 items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
          <Envelope size={16} weight="fill" className="text-blue-600 dark:text-blue-400" />
        </div>

        <div className="min-w-0 flex-1 pr-6">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
            {t`Verify your email to enable password recovery.`}{" "}
            <span className="font-normal text-blue-700/80 dark:text-blue-400/70">
              {t`We sent a link to ${userEmail}. Verifying is optional, but recommended.`}
            </span>
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <button
          disabled={loading}
          onClick={handleResend}
          className={cn(
            "h-7 rounded-md border border-blue-300 bg-white/80 px-3 text-xs font-medium text-blue-700 transition-colors hover:bg-white disabled:opacity-50",
            "dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50",
          )}
        >
          {loading ? t`Sending…` : t`Resend email`}
        </button>
      </div>

      <button
        onClick={handleDismiss}
        aria-label={t`Dismiss`}
        className="absolute right-2 top-2 rounded-full p-1 text-blue-400 transition-colors hover:text-blue-600"
      >
        <X size={14} />
      </button>
    </div>
  );
};
